import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Typography } from '@gapartners/ui';
import type { DisplayCall } from './CallHistoryCardTypes';
import { getCallDisplayName } from './callHistoryUtils';
import { ClientSpaceWorkspaceLink } from './ClientSpaceWorkspaceLink';

export interface CallHistoryCallsSectionProps {
  displayCalls: DisplayCall[];
  isLoading: boolean;
  hasLoadedInitial: boolean;
  error: unknown;
  isFetching: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onCallClick: (call: DisplayCall) => void;
  onOpenCaseId: (
    caseId: number,
    contactId: string,
    workspaceId?: number
  ) => void;
  getOutcomeColor: (outcome: string | null) => {
    bgcolor: string;
    color: string;
  };
  getCaseStatusColor: (caseStatus: string | null) => {
    bgcolor: string;
    color: string;
  };
  emptyMessageKey?: string;
  useUnknownCallerFallback?: boolean;
}

export function CallHistoryCallsSection(props: CallHistoryCallsSectionProps) {
  const {
    displayCalls,
    isLoading,
    hasLoadedInitial,
    error,
    isFetching,
    hasMore,
    onLoadMore,
    onCallClick,
    onOpenCaseId,
    getOutcomeColor,
    getCaseStatusColor,
    emptyMessageKey = 'components.contactCenter.noCallsFound',
    useUnknownCallerFallback = false,
  } = props;

  const { t } = useTranslation();
  const unknownCallerLabel = t('components.contactCenter.unknownCaller');
  const phoneLine = (call: DisplayCall) => {
    if (call.type === 'chat') {
      return null;
    }
    return call.clientDetails?.phoneNumber?.trim()
      ? call.clientDetails.phoneNumber
      : call.phoneNumber;
  };
  return (
    <>
      {isLoading && !hasLoadedInitial ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 128,
          }}
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography variant="body2" color="error.main">
          {t('components.contactCenter.errorLoadingCalls')}
        </Typography>
      ) : displayCalls.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {t(emptyMessageKey)}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {displayCalls.map(call => (
            <Box
              key={call.id}
              onClick={() => onCallClick(call)}
              sx={{
                cursor: 'pointer',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                '&:hover': { bgcolor: 'action.hover' },
                transition: 'background-color 0.2s',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 0.5,
                }}
              >
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    color="text.primary"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 260,
                    }}
                  >
                    {useUnknownCallerFallback
                      ? getCallDisplayName(call, unknownCallerLabel)
                      : call.clientDetails?.name?.trim()
                        ? call.clientDetails.name
                        : call.phoneNumber}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 260,
                    }}
                  >
                    {phoneLine(call)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {call.dateTime}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <ClientSpaceWorkspaceLink workspaceId={call.workspaceId} />
                  {call.caseId && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() =>
                        onOpenCaseId(
                          Number(call.caseId),
                          call.contactId,
                          call.workspaceId
                            ? Number(call.workspaceId)
                            : undefined
                        )
                      }
                      sx={{
                        cursor: 'pointer',
                        p: 0,
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                      }}
                    >
                      {t('components.contactCenter.caseId')}: {call.caseId}
                    </Button>
                  )}
                </Box>
              </Box>

              {call.reason && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    mb: 0.5,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {t('components.contactCenter.reason')}: {call.reason}
                </Typography>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  alignItems: 'center',
                  mt: 0.5,
                }}
              >
                {call.outcome && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.65rem',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 1,
                      ...getOutcomeColor(call.outcome),
                    }}
                  >
                    {call.outcome}
                  </Box>
                )}
                {call.caseStatus && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.65rem',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 1,
                      ...getCaseStatusColor(call.caseStatus),
                    }}
                  >
                    {call.caseStatus}
                  </Box>
                )}
                {call.type === 'call' && call.isCallback && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.65rem',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: '#fef3c7',
                      color: '#92400e',
                    }}
                  >
                    {t('components.amazonConnect.callbackLabel')}
                  </Box>
                )}
                <Box sx={{ flex: 1 }} />
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.7rem',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor:
                      call.type === 'chat' ? 'success.light' : 'primary.light',
                    color:
                      call.type === 'chat' ? 'success.dark' : 'primary.dark',
                  }}
                >
                  {call.type === 'chat'
                    ? t('components.amazonConnect.chatLabel')
                    : t('components.contactCenter.call')}
                </Box>
              </Box>
            </Box>
          ))}

          {hasMore && (
            <Button
              size="small"
              variant="text"
              onClick={onLoadMore}
              disabled={isFetching}
              sx={{ mt: 1 }}
            >
              {isFetching ? (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              ) : null}
              {t('common.loadMore')}
            </Button>
          )}
        </Box>
      )}
    </>
  );
}
